import React, { useState, useCallback, useEffect, useReducer, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Divider,
  IconButton,
  Card,
  Paper,
  Tabs,
  Tab,
  Link,
  CircularProgress,
  Alert,
  Fade,
} from '@mui/material';
import { CameraService } from '../../service/camera.service';
import { DashboardService } from '../../service/dashboard.service';
import { useNavigate } from 'react-router-dom';
import KameraItem from '../../components/KameraItem';
import AddIcon from '@mui/icons-material/Add';
import VideocamIcon from '@mui/icons-material/Videocam';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

/* ------------------------------------------------------------------ */
/* GLOBALS (provided via <script> tags in index.html)                 */
/* ------------------------------------------------------------------ */
declare const Janus: any;

/* ------------------------------------------------------------------ */
/* CONSTANTS                                                          */
/* ------------------------------------------------------------------ */
const FALLBACK_W = 680;          // ← default if metadata never fires
const FALLBACK_H = 360;

/* ------------------------------------------------------------------ */
/* TYPES                                                              */
/* ------------------------------------------------------------------ */
interface Config {
  subdomain: string;
  turnIp: string;
}

interface StreamMeta {
  id: number;
  description: string;
}

type StreamStatus =
  | 'initial'
  | 'connecting'
  | 'started'
  | 'stopped'
  | 'error';

interface StreamState {
  meta: StreamMeta;
  status: StreamStatus;
  error?: string;
  remoteStream?: MediaStream;
  videoSize?: { w: number; h: number };
  boxes?: number[][];                // [ [x1,y1,x2,y2], … ]
}

interface State {
  loadingConfig: boolean;
  configError: string | null;
  config: Config | null;
  streams: Record<number, StreamState>;
  wsReady: boolean;
}

type Action =
  | { type: 'CONFIG_LOADING' }
  | { type: 'CONFIG_SUCCESS'; payload: Config }
  | { type: 'CONFIG_ERROR'; payload: string }
  | { type: 'STREAM_ADD'; payload: StreamMeta }
  | {
    type: 'STREAM_STATUS';
    payload: {
      id: number;
      status: StreamStatus;
      error?: string;
      remoteStream?: MediaStream;
    };
  }
  | { type: 'STREAM_REMOVE'; payload: { id: number } }
  | { type: 'SET_VIDEO_SIZE'; payload: { id: number; w: number; h: number } }
  | { type: 'UPDATE_BOXES'; payload: { camera: string; boxes: number[][] } }
  | { type: 'WS_READY' }
  | { type: 'WS_CLOSE' };

/* ------------------------------------------------------------------ */
/* REDUCER                                                            */
/* ------------------------------------------------------------------ */
const initialState: State = {
  loadingConfig: true,
  configError: null,
  config: null,
  streams: {},
  wsReady: false,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'CONFIG_LOADING':
      return { ...state, loadingConfig: true, configError: null };

    case 'CONFIG_SUCCESS':
      return { ...state, loadingConfig: false, config: action.payload };

    case 'CONFIG_ERROR':
      return { ...state, loadingConfig: false, configError: action.payload };

    case 'STREAM_ADD':
      return {
        ...state,
        streams: {
          ...state.streams,
          [action.payload.id]: { meta: action.payload, status: 'initial' },
        },
      };

    case 'STREAM_STATUS':
      return {
        ...state,
        streams: {
          ...state.streams,
          [action.payload.id]: {
            ...state.streams[action.payload.id],
            status: action.payload.status,
            error: action.payload.error,
            remoteStream:
              action.payload.remoteStream ??
              state.streams[action.payload.id]?.remoteStream,
          },
        },
      };

    case 'STREAM_REMOVE': {
      const { [action.payload.id]: _gone, ...rest } = state.streams;
      return { ...state, streams: rest };
    }

    case 'SET_VIDEO_SIZE': {
      const { id, w, h } = action.payload;
      return {
        ...state,
        streams: {
          ...state.streams,
          [id]: { ...state.streams[id], videoSize: { w, h } },
        },
      };
    }

    case 'UPDATE_BOXES': {
      const { camera, boxes } = action.payload;

      /* 1️⃣ exact match first (case-insensitive, accent-insensitive) */
      let match = Object.values(state.streams).find(
        (s) =>
          !s.meta.description.startsWith('BBox') &&
          s.meta.description.localeCompare(camera, undefined, {
            sensitivity: 'accent',
          }) === 0,
      );

      /* 2️⃣ fuzzy fallback                                        */
      if (!match) {
        match = Object.values(state.streams).find(
          (s) =>
            !s.meta.description.startsWith('BBox') &&
            (s.meta.description.toLowerCase() === camera.toLowerCase() ||
              camera.toLowerCase().includes(s.meta.description.toLowerCase()) ||
              s.meta.description
                .toLowerCase()
                .includes(camera.toLowerCase())),
        );
      }

      if (!match) {
        console.warn(
          '[BBox] No matching video stream found for',
          camera,
          '; existing streams:',
          Object.values(state.streams)
            .filter((s) => !s.meta.description.startsWith('BBox'))
            .map((s) => s.meta.description),
        );
        return state;
      }

      const matchId = match.meta.id;
      const old = state.streams[matchId];
      return {
        ...state,
        streams: {
          ...state.streams,
          [matchId]: {
            ...old,
            boxes,
            videoSize: old.videoSize ?? { w: FALLBACK_W, h: FALLBACK_H },
          },
        },
      };
    }

    case 'WS_READY':
      return { ...state, wsReady: true };

    case 'WS_CLOSE':
      return { ...state, wsReady: false };

    default:
      return state;
  }
}

const CamerasView: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0); // 0 entspricht "Kameras" im Tab-Menü
  const [navVisible, setNavVisible] = useState(true);
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(reducer, initialState);

  /* ---- refs that outlive renders -------------------------------- */
  const janusRef   = useRef<any>(null);
  const handlesRef = useRef<Record<number, any>>({});
  const wsRef      = useRef<WebSocket | null>(null);
  const pcsRef     = useRef<Record<string, RTCPeerConnection>>({});

  /* -------------------- helpers ---------------------------------- */
  const updateBoxes = useCallback(
    (camera: string, boxes: number[][]) =>
      dispatch({ type: 'UPDATE_BOXES', payload: { camera, boxes } }),
    [],
  );

  /** Convert either *pixel* or *normalised* values to % */
  const asPerc = (value: number, max: number) =>
    value <= 1 ? value * 100 : (value / max) * 100;

  /* -------------------- Data-channel via external hub ------------- */
  const subscribeToDataStream = useCallback(
    async (streamId: string) => {
      if (!state.config) return;

      const token = localStorage.getItem('token');
      if (!token || pcsRef.current[streamId]) return;

      const { turnIp, subdomain } = state.config;
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          {
            urls: `turn:${turnIp}:3478`,
            username: 'myuser',
            credential: 'mypass',
          },
        ],
      });
      pcsRef.current[streamId] = pc;

      const dc = pc.createDataChannel('data');
      dc.onopen = () => console.log(`✅ DC open for ext #${streamId}`);
      dc.onmessage = (e: MessageEvent) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.camera && Array.isArray(msg.boxes))
            updateBoxes(msg.camera, msg.boxes);
        } catch {
          /* ignore JSON errors */
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (['closed', 'failed', 'disconnected'].includes(pc.iceConnectionState)) {
          pc.close();
          delete pcsRef.current[streamId];
        }
      };

      /* --- SDP round-trip to the backend ------------------------ */
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const resp = await fetch(
        `https://${subdomain}.washprotech.com:8080/client_subscribe`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            stream_id: streamId,
            sdp: pc.localDescription?.sdp,
            type: pc.localDescription?.type,
          }),
        },
      );
      if (!resp.ok) {
        pc.close();
        delete pcsRef.current[streamId];
        return;
      }

      const answer = await resp.json();
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
    },
    [state.config, updateBoxes],
  );

  /* ------------------------- WebSocket --------------------------- */
  const initWebSocket = useCallback(() => {
    if (!state.config) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(
      `${proto}://${state.config.subdomain}.washprotech.com:8080/ws?token=${token}`,
    );
    wsRef.current = ws;

    ws.onopen = () => {
      dispatch({ type: 'WS_READY' });
      ws.send(JSON.stringify({ action: 'list_streams' }));
    };

    ws.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d.action === 'streams')      d.streams.forEach(subscribeToDataStream);
        if (d.action === 'new_stream')   subscribeToDataStream(d.stream_id);
      } catch { /* ignore */ }
    };

    ws.onclose = () => dispatch({ type: 'WS_CLOSE' });
  }, [state.config, subscribeToDataStream]);

  /* -------------------------- Janus ------------------------------ */
  const initJanus = useCallback(() => {
    if (!state.config) return;
    const { subdomain, turnIp } = state.config;

    Janus.init({
      debug: 'warn',
      callback: () => {
        if (!Janus.isWebrtcSupported()) {
          dispatch({ type: 'CONFIG_ERROR', payload: 'WebRTC unsupported' });
          return;
        }

        janusRef.current = new Janus({
          server: `https://${subdomain}.api.washprotech.com:8889/janus`,
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            {
              urls: `turn:${turnIp}:3478?transport=udp`,
              username: 'myuser',
              credential: 'mypass',
            },
          ],
          success: () => attachListHandle(),
          error  : (e: any) =>
            dispatch({ type: 'CONFIG_ERROR', payload: String(e) }),
        });
      },
    });
  }, [state.config]);

  /* --------------------- Helpers for Janus ----------------------- */
  const attachListHandle = () => {
    const janus = janusRef.current;
    if (!janus) return;

    janus.attach({
      plugin   : 'janus.plugin.streaming',
      opaqueId : `list-${Janus.randomString(8)}`,

      success: (h: any) => {
        h.send({
          message: { request: 'list' },
          success: (res: any) => {
            (res.list as StreamMeta[])
              ?.forEach((m) =>
                dispatch({ type: 'STREAM_ADD', payload: m }),
              );
            h.detach();
          },
        });
      },
    });
  };

  const attachStream = useCallback(
    (stream: StreamMeta) => {
      const janus = janusRef.current;
      if (!janus) return;

      dispatch({
        type   : 'STREAM_STATUS',
        payload: { id: stream.id, status: 'connecting' },
      });

      janus.attach({
        plugin   : 'janus.plugin.streaming',
        opaqueId : `watch-${stream.id}-${Janus.randomString(8)}`,

        success: (h: any) => {
          handlesRef.current[stream.id] = h;
          h.send({ message: { request: 'watch', id: stream.id } });

          /* ---------- if this is a BBox data stream -------------- */
          if (stream.description.startsWith('BBox')) {
            const target = stream.description.match(/\[(.*)\]/)?.[1] ?? '';

            h.ondataopen = () =>
              console.log(`📡 helper DC open #${stream.id}`);

            h.ondata = (raw: string) => {
              try {
                const { boxes } = JSON.parse(raw);
                if (Array.isArray(boxes)) updateBoxes(target, boxes);
              } catch { /* ignore parse errors */ }
            };
          }
        },

        onmessage: (_msg: any, jsep: any) => {
          if (!jsep) return;

          handlesRef.current[stream.id].createAnswer({
            jsep,
            tracks : [
              { type: 'video', capture: false, recv: true },
              { type: 'data' }
            ],
            success: (ans: any) =>
              handlesRef.current[stream.id].send({
                message: { request: 'start' },
                jsep   : ans,
              }),
          });
        },

        onremotetrack: (
          track: MediaStreamTrack,
          _mid: string,
          on: boolean,
        ) => {
          if (!on || track.kind !== 'video') return;

          const media = new MediaStream([track]);

          /** width / height as offered in the SDP (may change later) */
          const stgs = track.getSettings();
          if (stgs.width && stgs.height)
            dispatch({
              type   : 'SET_VIDEO_SIZE',
              payload: { id: stream.id, w: stgs.width, h: stgs.height },
            });

          dispatch({
            type   : 'STREAM_STATUS',
            payload: {
              id          : stream.id,
              status      : 'started',
              remoteStream: media,
            },
          });
        },

        oncleanup: () =>
          dispatch({ type: 'STREAM_REMOVE', payload: { id: stream.id } }),
      });
    },
    [updateBoxes],
  );

  /* ---------------------------- effects -------------------------- */
  /* 1) load backend config ---------------------------------------- */
  useEffect(() => {
    dispatch({ type: 'CONFIG_LOADING' });

    DashboardService.getDashboard()
      .then((d) => {
        if (!d.subdomain || !d.turnIp) throw new Error('Bad cfg');
        dispatch({
          type   : 'CONFIG_SUCCESS',
          payload: { subdomain: d.subdomain, turnIp: d.turnIp },
        });
      })
      .catch((e: Error) =>
        dispatch({ type: 'CONFIG_ERROR', payload: e.message }),
      );
  }, []);

  /* 2) once config is present, start Janus + WebSocket ------------ */
  useEffect(() => {
    if (state.config && !state.configError) {
      if (!janusRef.current) initJanus();
      if (!wsRef.current)    initWebSocket();
    }
  }, [state.config, state.configError, initJanus, initWebSocket]);

  /* 3) whenever new stream meta is added, attach ------------------ */
  useEffect(() => {
    Object.values(state.streams).forEach(
      (s) => s.status === 'initial' && attachStream(s.meta),
    );
  }, [state.streams, attachStream]);

  /* 4) mount MediaStream -> <video> and capture **displayed** size */
  useEffect(() => {
    Object.values(state.streams).forEach((s) => {
      if (!s.remoteStream) return;

      const v = document.getElementById(
        `video-${s.meta.id}`,
      ) as HTMLVideoElement | null;

      if (v && v.srcObject !== s.remoteStream) {
        v.srcObject = s.remoteStream;

        v.onloadedmetadata = () => {
          const rect = v.getBoundingClientRect();
          dispatch({
            type   : 'SET_VIDEO_SIZE',
            payload: {
              id: s.meta.id,
              w : rect.width,
              h : rect.height,
            },
          });
        };
      }
    });
  }, [state.streams]);

  /* 5) global cleanup -------------------------------------------- */
  useEffect(
    () => () => {
      Object.values(handlesRef.current).forEach((h) => h.detach?.());
      janusRef.current?.destroy?.();
      wsRef.current?.close();
      Object.values(pcsRef.current).forEach((pc) => pc.close());
    },
    [],
  );

  // Navigation related code
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    
    // Navigation zu anderen Seiten basierend auf Tab-Auswahl
    switch (newValue) {
      case 0:
        // Wir sind bereits auf der Kameras-Seite
        break;
      case 1:
        navigate('/meldungen');  // Meldungen
        break;
      case 2:
        navigate('/einstellungen');  // Einstellungen
        break;
    }
  };

  const toggleNavVisibility = () => {
    setNavVisible(!navVisible);
  };

  /* --------------------------- render ---------------------------- */
  // Render monitoring content
  const renderMonitoringContent = () => {
    if (state.loadingConfig) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Lade Konfiguration...</Typography>
        </Box>
      );
    }

    if (state.configError) {
      return (
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{state.configError}</Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'center',
          }}
        >
          {Object.values(state.streams)
            .filter((s) => !s.meta.description.startsWith('BBox'))
            .map((s) => {
              const w = s.videoSize?.w ?? FALLBACK_W;
              const h = s.videoSize?.h ?? FALLBACK_H;

              return (
                <Box
                  key={s.meta.id}
                  sx={{
                    position: 'relative',
                    flex: '1 1 320px',
                    maxWidth: navVisible ? 400 : 500,
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    p: 1,
                    transition: 'max-width 0.3s ease',
                  }}
                >
                  {/* ---- title ------------------------------------- */}
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}
                  >
                    {s.meta.description}
                  </Typography>

                  {/* ---- failure notice ---------------------------- */}
                  {s.status === 'error' && (
                    <Alert severity="error">{s.error}</Alert>
                  )}

                  {/* ---- video element ----------------------------- */}
                  <video
                    id={`video-${s.meta.id}`}
                    style={{
                      width: '100%',
                      display: s.status === 'started' ? 'block' : 'none',
                    }}
                    playsInline
                    muted
                    autoPlay
                  />

                  {/* ---- overlay with bounding-boxes --------------- */}
                  {s.status === 'started' && !!s.boxes?.length && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                      }}
                    >
                      {s.boxes.map(([x1, y1, x2, y2], i) => (
                        <Box
                          key={i}
                          sx={{
                            position: 'absolute',
                            border: '2px solid #0f0',
                            left: `${asPerc(x1, w)}%`,
                            top: `${asPerc(y1, h)}%`,
                            width: `${asPerc(x2 - x1, w)}%`,
                            height: `${asPerc(y2 - y1, h)}%`,
                            boxSizing: 'border-box',
                            transition: 'all .1s linear',
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {/* ---- placeholder text while connecting --------- */}
                  {s.status !== 'started' && s.status !== 'error' && (
                    <Typography
                      variant="body2"
                      sx={{ py: 4, textAlign: 'center' }}
                    >
                      {s.status === 'connecting'
                        ? 'Verbinde...'
                        : s.status === 'stopped'
                        ? 'Stream gestoppt'
                        : 'Warte...'}
                    </Typography>
                  )}
                </Box>
              );
            })}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Hide/Show Navigation Button (always visible) */}
      <Box
        sx={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          zIndex: 1100,
        }}
      >
        <IconButton
          onClick={toggleNavVisibility}
          color="primary"
          sx={{ 
            bgcolor: 'white',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            '&:hover': { bgcolor: 'white' }
          }}
        >
          {navVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </Box>
      
      {/* Header mit Navigation */}
      <Fade in={navVisible}>
        <Box component="header" sx={{ mb: 0, display: navVisible ? 'block' : 'none' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
            <Link 
              component="button" 
              onClick={() => navigate('/')}
              underline="none"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Typography variant="h5" component="h1" sx={{ flexGrow: 1, fontWeight: 'bold', color: 'text.primary' }}>
                WashProTech
              </Typography>
            </Link>
          </Box>
          
          <Paper 
            sx={{ 
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              marginBottom: 0
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="Navigation"
              variant="fullWidth"
              centered
              sx={{
                marginBottom: 0,
                paddingBottom: 0,
                '& .MuiTabs-scroller': {
                  marginBottom: '0px !important'
                },
                '& .MuiTab-root': {
                  py: 2,
                  minHeight: '60px',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    bgcolor: 'transparent',
                    transition: 'background-color 0.3s'
                  },
                  '&.Mui-selected': {
                    fontWeight: 'bold',
                    color: 'primary.main',
                    '&::after': {
                      bgcolor: 'primary.main'
                    }
                  }
                }
              }}
            >
              <Tab icon={<VideocamIcon />} label="Kameras" />
              <Tab icon={<NotificationsIcon />} label="Meldungen" />
              <Tab icon={<SettingsIcon />} label="Einstellung" />
            </Tabs>
          </Paper>
        </Container>
      </Box>
      </Fade>

      {/* Main Content with conditional padding based on navbar visibility */}
      <Container 
        maxWidth="xl" 
        sx={{ 
          flexGrow: 1, 
          mb: 4, 
          pt: navVisible ? 2 : 0,
          mt: navVisible ? 0 : 2, 
          transition: 'padding-top 0.3s ease, margin-top 0.3s ease'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h5" component="h1">
            Kamera-Monitoring
              </Typography>
        </Box>
        
        {renderMonitoringContent()}
      </Container>
    </Box>
  );
};

export default CamerasView; 